import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AttachDealContactInput,
  CreateDealInput,
  CreateDealLineItemInput,
  DealContact,
  DealLineItem,
  DealPipelineSummaryResponse,
  DealResponse,
  DealStageHistoryEntry,
  ListDealsQuery,
  MoveDealStageInput,
  PipelineResponse,
  UpdateDealInput,
} from '@orion/contracts';
import { api, getPage } from '../../lib/api-client.js';

function toQueryString(query: Partial<ListDealsQuery>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function usePipelinesQuery() {
  return useQuery({ queryKey: ['pipelines'], queryFn: () => api.get<PipelineResponse[]>('/pipelines') });
}

export function usePipelineSummaryQuery(pipelineId: string | undefined) {
  return useQuery({
    queryKey: ['deals', 'pipeline-summary', pipelineId],
    queryFn: () => api.get<DealPipelineSummaryResponse>(`/deals/pipeline-summary?pipelineId=${pipelineId}`),
    enabled: Boolean(pipelineId),
  });
}

export function useLossReasonsQuery() {
  return useQuery({
    queryKey: ['loss-reasons'],
    queryFn: () => api.get<Array<{ id: string; name: string }>>('/pipelines/loss-reasons'),
  });
}

export function useDealsQuery(query: Partial<ListDealsQuery>) {
  return useQuery({
    queryKey: ['deals', query],
    queryFn: () => getPage<DealResponse[]>(`/deals${toQueryString(query)}`),
    placeholderData: (prev) => prev,
  });
}

export function useDealQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['deals', id],
    queryFn: () => api.get<DealResponse>(`/deals/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDealInput) => api.post<DealResponse>('/deals', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deals'] }),
  });
}

export function useMoveDealStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: MoveDealStageInput }) => api.patch<DealResponse>(`/deals/${id}/stage`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deals'] }),
  });
}

export function useUpdateDeal(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateDealInput) => api.patch<DealResponse>(`/deals/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deals'] }),
  });
}

export function useDealStageHistoryQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['deals', id, 'stage-history'],
    queryFn: () => api.get<DealStageHistoryEntry[]>(`/deals/${id}/stage-history`),
    enabled: Boolean(id),
  });
}

export function useDealContactsQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['deals', id, 'contacts'],
    queryFn: () => api.get<DealContact[]>(`/deals/${id}/contacts`),
    enabled: Boolean(id),
  });
}

export function useDealLineItemsQuery(id: string | undefined) {
  return useQuery({
    queryKey: ['deals', id, 'line-items'],
    queryFn: () => api.get<DealLineItem[]>(`/deals/${id}/line-items`),
    enabled: Boolean(id),
  });
}

export function useAddDealLineItem(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDealLineItemInput) => api.post<DealLineItem>(`/deals/${id}/line-items`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deals', id, 'line-items'] }),
  });
}

export function useAttachDealContact(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AttachDealContactInput) => api.post<DealContact>(`/deals/${id}/contacts`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['deals', id, 'contacts'] }),
  });
}
